import { defineStore } from "pinia";
import { markRaw, toRaw } from "vue";
import { findStopTimeRow, invalidateGtfsFeedIndexes, patchStopTimeIndex, scheduleFeedIndexRebuild, } from "@/composables/gtfs/useGTFSFeedIndexes.js";
import { createFeedEntry, createLocalFeedId, } from "@/composables/gtfs/gtfsFeedTypes.js";
const defaultStats = () => ({
    totalFeeds: 0,
    totalStops: 0,
    totalRoutes: 0,
    totalTrips: 0,
    totalSize: 0,
    validFeeds: 0,
});
const defaultTableCounts = () => ({
    agency: 0,
    routes: 0,
    stops: 0,
    trips: 0,
    stop_times: 0,
    calendar: 0,
    calendar_dates: 0,
    shapes: 0,
    feed_info: 0,
});
export const HISTORY_LIMIT = 100;
/** Büyük tablolar — tam kopya yerine yalnızca değişen satırlar (fark) tutulur */
const DIFF_HISTORY_TABLES = new Set([
    "stop_times.txt",
    "shapes.txt",
    "trips.txt",
    "stops.txt",
]);
/** Vue deep proxy yapma — bellek/GC felçini önler */
const MARK_RAW_TABLES = new Set([
    "stop_times.txt",
    "shapes.txt",
    "trips.txt",
    "stops.txt",
]);
let historySeq = 0;
const TABLE_NOUNS = {
    agency: "kurum",
    routes: "hat",
    stops: "durak",
    trips: "sefer",
    stop_times: "sefer saati",
    calendar: "servis",
    calendar_dates: "istisna günü",
    shapes: "şekil noktası",
    feed_info: "feed bilgisi",
    frequencies: "sıklık kaydı",
    transfers: "aktarma",
};
function shortTableName(name) {
    return String(name || "").replace(/\.txt$/, "");
}
function capitalizeTr(text) {
    if (!text)
        return text;
    return text.charAt(0).toLocaleUpperCase("tr-TR") + text.slice(1);
}
/** "3 hat silindi", "Durak eklendi" */
export function historyCountLabel(tableName, count, verb) {
    const noun = TABLE_NOUNS[shortTableName(tableName)] || "kayıt";
    return count > 1 ? `${count} ${noun} ${verb}` : capitalizeTr(`${noun} ${verb}`);
}
function updateLabel(tableName, patch) {
    const short = shortTableName(tableName);
    const keys = Object.keys(patch || {});
    if (short === "stops" &&
        keys.length > 0 &&
        keys.every((k) => k === "stop_lat" || k === "stop_lon")) {
        return "Durak taşındı";
    }
    return historyCountLabel(short, 1, "güncellendi");
}
function tableMetaOf(table) {
    if (!table)
        return {};
    const rest = { ...table };
    delete rest.sample_data;
    if (Array.isArray(rest.columns))
        rest.columns = [...rest.columns];
    return rest;
}
function cloneTable(table) {
    const raw = toRaw(table);
    const rows = Array.isArray(raw.sample_data) ? toRaw(raw.sample_data) : [];
    return {
        ...tableMetaOf(raw),
        sample_data: rows.map((r) => ({ ...toRaw(r) })),
    };
}
/** Büyük dizide spread/splice(...x) stack overflow yapar — döngüyle yerinde değiştir */
function replaceRowsInPlace(rows, next) {
    rows.length = 0;
    for (let i = 0; i < next.length; i++)
        rows.push(next[i]);
}
/**
 * Önceki ve sonraki satır dizisinden fark üretir. Değişmeyen satırlar aynı
 * nesne referansıdır (mutasyonlar satırı yerinde değiştirmez, yenisini koyar).
 */
export function diffRows(before, after) {
    const removed = [];
    const added = [];
    const minLen = Math.min(before.length, after.length);
    let start = 0;
    while (start < minLen && before[start] === after[start])
        start++;
    let endB = before.length;
    let endA = after.length;
    while (endB > start && endA > start && before[endB - 1] === after[endA - 1]) {
        endB--;
        endA--;
    }
    if (endB - start === endA - start) {
        for (let i = start; i < endB; i++) {
            if (before[i] !== after[i]) {
                removed.push([i, before[i]]);
                added.push([i, after[i]]);
            }
        }
        return { removed, added };
    }
    const afterSet = new Set();
    for (let i = start; i < endA; i++)
        afterSet.add(after[i]);
    const beforeSet = new Set();
    for (let i = start; i < endB; i++)
        beforeSet.add(before[i]);
    const keptB = [];
    for (let i = start; i < endB; i++) {
        if (afterSet.has(before[i]))
            keptB.push(before[i]);
        else
            removed.push([i, before[i]]);
    }
    let k = 0;
    let orderOk = true;
    for (let i = start; i < endA; i++) {
        if (beforeSet.has(after[i])) {
            if (keptB[k] !== after[i])
                orderOk = false;
            k++;
        }
        else {
            added.push([i, after[i]]);
        }
    }
    if (orderOk && k === keptB.length)
        return { removed, added };
    // Sıra değişmiş — orta bölgeyi bütünüyle değiştir
    removed.length = 0;
    added.length = 0;
    for (let i = start; i < endB; i++)
        removed.push([i, before[i]]);
    for (let i = start; i < endA; i++)
        added.push([i, after[i]]);
    return { removed, added };
}
/** `drop` index'lerini çıkarır, `insert` satırlarını hedef index'lerine koyar */
export function applyRowDiff(rows, drop, insert) {
    if (!drop.length && !insert.length)
        return;
    if (drop.length === insert.length &&
        drop.every(([i], n) => i === insert[n][0])) {
        for (let n = 0; n < insert.length; n++)
            rows[insert[n][0]] = insert[n][1];
        return;
    }
    const kept = [];
    let d = 0;
    for (let i = 0; i < rows.length; i++) {
        if (d < drop.length && drop[d][0] === i) {
            d++;
            continue;
        }
        kept.push(rows[i]);
    }
    const total = kept.length + insert.length;
    rows.length = 0;
    let k = 0;
    let n = 0;
    for (let i = 0; i < total; i++) {
        if (n < insert.length && insert[n][0] === i)
            rows.push(insert[n++][1]);
        else
            rows.push(kept[k++]);
    }
}
function markHeavySampleData(tables) {
    if (!tables)
        return;
    Object.entries(tables).forEach(([key, table]) => {
        if (!table || !MARK_RAW_TABLES.has(key))
            return;
        if (Array.isArray(table.sample_data)) {
            table.sample_data = markRaw(table.sample_data);
        }
    });
}
function extractTableCounts(tables = {}) {
    const counts = defaultTableCounts();
    Object.keys(counts).forEach((name) => {
        const info = tables[`${name}.txt`];
        counts[name] = info?.record_count ?? info?.row_count ?? 0;
    });
    return counts;
}
function isAgencyComplete(tables = {}) {
    const agency = tables["agency.txt"];
    if (!agency?.exists && !(agency?.record_count > 0))
        return false;
    const rows = agency.sample_data || [];
    if (!rows.length)
        return (agency.record_count || 0) > 0;
    const row = rows[0] || {};
    return Boolean(row.agency_name && row.agency_url && row.agency_timezone);
}
function isFeedInfoComplete(tables = {}) {
    const feed = tables["feed_info.txt"];
    return Boolean(feed?.exists || (feed?.record_count || 0) > 0);
}
/**
 * GTFS modül state (Pinia).
 * v2 nav sayıları bu store'dan okunur; inspect sonucu setInspectResult ile yazılır.
 * Çoklu feed: feeds[] registry + activeFeedId; editör yalnızca aktif feed'i hydrate eder.
 */
export const useGtfsStore = defineStore("gtfs", {
    state: () => ({
        activeTab: "builder",
        builderCurrentStep: 1,
        isProcessing: false,
        currentGTFSData: null,
        feedFileName: "",
        stats: defaultStats(),
        tableCounts: defaultTableCounts(),
        agencyComplete: false,
        feedInfoComplete: false,
        pendingDirty: false,
        historyPast: [],
        historyFuture: [],
        /** Henüz farka dönüşmemiş son kayıt (aynı tick içindeki mutasyonlar birleşir) */
        _openEntry: null,
        hydratingTables: false,
        tableLoadCacheKey: "",
        /** UI'nın ağır tabloları yeniden okuması için (markRaw yüzünden) */
        tableEpoch: 0,
        _batchDepth: 0,
        _batchTables: [],
        _pendingInvalidate: false,
        /** Çoklu GTFS registry */
        feeds: [],
        activeFeedId: null,
        analysisHydrating: false,
        /** Haritada gizlenen hatlar. Düzenleme değildir: geçmişe ve dışa aktarmaya girmez. */
        hiddenRouteIds: [],
    }),
    getters: {
        hasGTFSData(state) {
            return (state.currentGTFSData !== null &&
                state.currentGTFSData.meta != null &&
                state.stats.totalStops > 0);
        },
        routesCount: (state) => state.tableCounts.routes,
        stopsCount: (state) => state.tableCounts.stops,
        calendarCount: (state) => state.tableCounts.calendar,
        isDirty: (state) => state.pendingDirty,
        canUndo: (state) => state.historyPast.length > 0,
        canRedo: (state) => state.historyFuture.length > 0,
        undoLabel: (state) => state.historyPast[state.historyPast.length - 1]?.label || "",
        redoLabel: (state) => state.historyFuture[state.historyFuture.length - 1]?.label || "",
        /** Silme toast'ındaki "Geri al" yalnızca bu kayıt hâlâ en üstteyse çalışır */
        undoEntryId: (state) => state.historyPast[state.historyPast.length - 1]?.id ?? null,
        activeFeed(state) {
            if (!state.activeFeedId)
                return null;
            return (state.feeds.find((f) => f.localId === state.activeFeedId) || null);
        },
        includedFeeds(state) {
            return state.feeds.filter((f) => f.includeInAnalysis);
        },
        feedCount: (state) => state.feeds.length,
    },
    actions: {
        clearHistory() {
            this.historyPast = [];
            this.historyFuture = [];
            this._openEntry = null;
        },
        _tablesRef() {
            const data = this.currentGTFSData;
            return data?.meta?.tables || null;
        },
        _captureState() {
            return {
                pendingDirty: this.pendingDirty,
                tableCounts: { ...this.tableCounts },
                stats: { ...this.stats },
                agencyComplete: this.agencyComplete,
                feedInfoComplete: this.feedInfoComplete,
            };
        },
        _restoreState(state) {
            this.pendingDirty = state.pendingDirty;
            this.tableCounts = { ...state.tableCounts };
            this.stats = { ...state.stats };
            this.agencyComplete = state.agencyComplete;
            this.feedInfoComplete = state.feedInfoComplete;
        },
        /** Mutasyondan önceki hali kayda ekler (tablo zaten kayıttaysa dokunmaz) */
        _captureTables(entry, tableNames) {
            const tables = this._tablesRef();
            if (!tables)
                return;
            for (const name of tableNames) {
                const key = name.endsWith(".txt") ? name : `${name}.txt`;
                if (entry.tables[key])
                    continue;
                const table = tables[key];
                if (!table)
                    continue;
                if (DIFF_HISTORY_TABLES.has(key)) {
                    const rows = Array.isArray(table.sample_data)
                        ? toRaw(table.sample_data)
                        : [];
                    entry.tables[key] = {
                        kind: "diff",
                        removed: [],
                        added: [],
                        metaBefore: tableMetaOf(toRaw(table)),
                        metaAfter: {},
                        baseRows: rows.slice(),
                    };
                }
                else {
                    entry.tables[key] = { kind: "snapshot", table: cloneTable(table) };
                }
            }
        },
        _commitOpenEntry() {
            const entry = this._openEntry;
            if (!entry)
                return;
            this._openEntry = null;
            const tables = this._tablesRef() || {};
            let changed = false;
            for (const [key, change] of Object.entries(entry.tables)) {
                if (change.kind !== "diff") {
                    changed = true;
                    continue;
                }
                const table = tables[key];
                const rows = Array.isArray(table?.sample_data)
                    ? toRaw(table.sample_data)
                    : [];
                const { removed, added } = diffRows(change.baseRows || [], rows);
                change.removed = removed;
                change.added = added;
                change.baseRows = null;
                change.metaAfter = tableMetaOf(table ? toRaw(table) : undefined);
                if (removed.length || added.length)
                    changed = true;
                else
                    delete entry.tables[key];
            }
            if (!changed) {
                const idx = this.historyPast.indexOf(entry);
                if (idx >= 0)
                    this.historyPast.splice(idx, 1);
            }
        },
        /** Geçmişe yazma noktası: aynı tick'te aynı etiketli mutasyonlar tek adım olur */
        _recordHistory(tableNames, label) {
            if (!this._tablesRef() || !tableNames.length)
                return;
            const open = this._openEntry;
            if (open && (this._batchDepth > 0 || open.label === label)) {
                if (!open.label)
                    open.label = label;
                this._captureTables(open, tableNames);
                return;
            }
            this._commitOpenEntry();
            const entry = markRaw({
                id: ++historySeq,
                label,
                tables: {},
                state: this._captureState(),
            });
            this._captureTables(entry, tableNames);
            if (!Object.keys(entry.tables).length && this._batchDepth === 0)
                return;
            this.historyPast.push(entry);
            if (this.historyPast.length > HISTORY_LIMIT) {
                this.historyPast.splice(0, this.historyPast.length - HISTORY_LIMIT);
            }
            this.historyFuture = [];
            this._openEntry = entry;
            if (this._batchDepth === 0) {
                queueMicrotask(() => {
                    if (this._openEntry === entry && this._batchDepth === 0) {
                        this._commitOpenEntry();
                    }
                });
            }
        },
        /** Açık kaydı hemen kapatır; sonraki mutasyon ayrı adım olur */
        commitHistory() {
            if (this._batchDepth === 0)
                this._commitOpenEntry();
        },
        _applyEntry(entry, direction) {
            const tables = this._tablesRef();
            if (!tables)
                return;
            const touched = [];
            for (const [key, change] of Object.entries(entry.tables)) {
                if (change.kind === "diff") {
                    const table = tables[key];
                    if (!table)
                        continue;
                    if (!Array.isArray(table.sample_data))
                        table.sample_data = [];
                    const rows = toRaw(table.sample_data);
                    if (direction === "undo") {
                        applyRowDiff(rows, change.added, change.removed);
                        Object.assign(table, change.metaBefore);
                    }
                    else {
                        applyRowDiff(rows, change.removed, change.added);
                        Object.assign(table, change.metaAfter);
                    }
                }
                else {
                    const current = tables[key] ? cloneTable(tables[key]) : null;
                    const restored = change.table;
                    if (MARK_RAW_TABLES.has(key) && Array.isArray(restored.sample_data)) {
                        restored.sample_data = markRaw(restored.sample_data);
                    }
                    tables[key] = restored;
                    if (current)
                        change.table = current;
                }
                touched.push(shortTableName(key));
            }
            const current = this._captureState();
            this._restoreState(entry.state);
            entry.state = current;
            touched.forEach((name) => this._syncTableCount(name));
            this._invalidateIndexes();
        },
        /** Kaydet sonrası: geçmiş korunur, geri alınan adım yeniden "kaydedilmemiş" olur */
        markSaved() {
            this.commitHistory();
            this.pendingDirty = false;
            for (const entry of this.historyPast)
                entry.state.pendingDirty = true;
            for (const entry of this.historyFuture)
                entry.state.pendingDirty = true;
        },
        _invalidateIndexes() {
            if (this._batchDepth > 0) {
                this._pendingInvalidate = true;
                return;
            }
            try {
                invalidateGtfsFeedIndexes();
            }
            catch {
                globalThis.__geFeedIndexEpoch =
                    (globalThis.__geFeedIndexEpoch ||
                        0) + 1;
            }
            this.tableEpoch += 1;
            // Worker rebuild — UI thread'i bloke etme; selection ensureIndexesReady bekler
            try {
                scheduleFeedIndexRebuild((name) => this.getTableRows(name));
            }
            catch {
                /* worker yoksa selection sync fallback kullanır */
            }
        },
        /** Batch içindeki tüm mutasyonlar tek geçmiş adımı olur; etiket yoksa ilk mutasyonunki */
        beginBatch(tableNames = [], label = "") {
            const names = Array.isArray(tableNames) ? tableNames : [tableNames];
            if (this._batchDepth === 0)
                this._commitOpenEntry();
            this._batchDepth += 1;
            if (this._batchDepth === 1 && names.filter(Boolean).length) {
                this._recordHistory(names.filter(Boolean), label);
            }
        },
        endBatch() {
            if (this._batchDepth > 0)
                this._batchDepth -= 1;
            if (this._batchDepth === 0) {
                this._batchTables = [];
                if (this._openEntry && !this._openEntry.label) {
                    this._openEntry.label = "Değişiklik";
                }
                this._commitOpenEntry();
                if (this._pendingInvalidate) {
                    this._pendingInvalidate = false;
                    this._invalidateIndexes();
                }
            }
        },
        /** Mutasyondan önce — yalnızca etkilenen tablolar */
        pushHistory(tableNames, label = "Değişiklik") {
            const names = Array.isArray(tableNames) ? tableNames : [tableNames];
            this._recordHistory(names.filter(Boolean), label);
        },
        undo() {
            this.commitHistory();
            const entry = this.historyPast.pop();
            if (!entry)
                return false;
            this._applyEntry(entry, "undo");
            this.historyFuture.push(entry);
            return true;
        },
        redo() {
            this.commitHistory();
            const entry = this.historyFuture.pop();
            if (!entry)
                return false;
            this._applyEntry(entry, "redo");
            this.historyPast.push(entry);
            return true;
        },
        /** Toast "Geri al": yalnızca ilgili kayıt hâlâ en üstteyse geri alır */
        undoIfLatest(entryId) {
            this.commitHistory();
            if (entryId == null || this.undoEntryId !== entryId)
                return false;
            return this.undo();
        },
        setTableRows(tableName, rows, columns) {
            const data = this.currentGTFSData;
            if (!data?.meta)
                return;
            if (!data.meta.tables)
                data.meta.tables = {};
            const key = tableName.endsWith(".txt") ? tableName : `${tableName}.txt`;
            const short = key.replace(/\.txt$/, "");
            const prev = data.meta.tables[key] || {};
            const rawRows = MARK_RAW_TABLES.has(key) ? markRaw(rows) : rows;
            data.meta.tables[key] = {
                ...prev,
                exists: true,
                sample_data: rawRows,
                record_count: rows.length,
                rows_deferred: false,
                columns: columns?.length ? columns : prev.columns || [],
            };
            this._syncTableCount(short);
            this._invalidateIndexes();
        },
        isTableDeferred(tableName) {
            const data = this.currentGTFSData;
            const key = tableName.endsWith(".txt") ? tableName : `${tableName}.txt`;
            return Boolean(data?.meta?.tables?.[key]?.rows_deferred);
        },
        /** Inspect / yükleme sonucunu nav ve paneller için senkronlar */
        setInspectResult(payload) {
            const result = payload.result || {};
            const meta = (result.meta || {});
            const tables = meta.tables || {};
            markHeavySampleData(tables);
            this.currentGTFSData = result;
            this.feedFileName = payload.fileName || this.feedFileName || "";
            this.tableCounts = extractTableCounts(tables);
            this.agencyComplete = isAgencyComplete(tables);
            this.feedInfoComplete = isFeedInfoComplete(tables);
            this.stats.totalStops =
                meta.stops_count ?? this.tableCounts.stops ?? 0;
            this.stats.totalRoutes =
                meta.routes_count ?? this.tableCounts.routes ?? 0;
            this.stats.totalTrips =
                meta.trips_count ?? this.tableCounts.trips ?? 0;
            this.pendingDirty = false;
            this.tableLoadCacheKey = "";
            this.clearHistory();
            this._invalidateIndexes();
            const zipFile = result.originalFile || null;
            const fm = payload.feedMeta || {};
            const counts = { ...this.tableCounts };
            const feedStats = {
                totalStops: this.stats.totalStops,
                totalRoutes: this.stats.totalRoutes,
                totalTrips: this.stats.totalTrips,
            };
            let targetId = fm.localId || this.activeFeedId;
            let entry = targetId
                ? this.feeds.find((f) => f.localId === targetId)
                : undefined;
            if (!entry && fm.replaceActive !== false && this.activeFeedId) {
                entry = this.feeds.find((f) => f.localId === this.activeFeedId);
                targetId = this.activeFeedId;
            }
            if (entry) {
                entry.fileName = payload.fileName || entry.fileName;
                entry.name =
                    fm.name ||
                        entry.name ||
                        payload.fileName ||
                        entry.fileName ||
                        "GTFS Feed";
                if (fm.dbId !== undefined)
                    entry.dbId = fm.dbId;
                if (fm.source)
                    entry.source = fm.source;
                if (fm.senaryoId !== undefined)
                    entry.senaryoId = fm.senaryoId;
                if (fm.gunTipi !== undefined)
                    entry.gunTipi = fm.gunTipi;
                if (fm.agencyName !== undefined)
                    entry.agencyName = fm.agencyName;
                entry.stats = feedStats;
                entry.tableCounts = counts;
                if (zipFile)
                    entry.zipFile = markRaw(zipFile);
                entry.analysisTables = null;
                entry.analysisHydrated = false;
                this.activeFeedId = entry.localId;
            }
            else {
                const created = createFeedEntry({
                    localId: targetId || createLocalFeedId(),
                    dbId: fm.dbId ?? null,
                    name: fm.name || payload.fileName || "GTFS Feed",
                    source: fm.source || "upload",
                    fileName: payload.fileName || "",
                    stats: feedStats,
                    tableCounts: counts,
                    zipFile: zipFile ? markRaw(zipFile) : null,
                    senaryoId: fm.senaryoId ?? null,
                    gunTipi: fm.gunTipi ?? null,
                    agencyName: fm.agencyName ?? null,
                });
                this.feeds.push(created);
                this.activeFeedId = created.localId;
            }
            this.stats.totalFeeds = this.feeds.length;
            this.stats.validFeeds = this.feeds.length;
        },
        syncActiveFeedMeta(patch = {}) {
            const entry = this.activeFeed;
            if (!entry)
                return;
            if (patch.name !== undefined)
                entry.name = patch.name;
            if (patch.fileName !== undefined) {
                entry.fileName = patch.fileName;
                this.feedFileName = patch.fileName;
            }
            if (patch.dbId !== undefined)
                entry.dbId = patch.dbId;
            if (patch.source !== undefined)
                entry.source = patch.source;
            if (patch.senaryoId !== undefined)
                entry.senaryoId = patch.senaryoId;
            if (patch.gunTipi !== undefined)
                entry.gunTipi = patch.gunTipi;
            if (patch.agencyName !== undefined)
                entry.agencyName = patch.agencyName;
            if (patch.zipFile)
                entry.zipFile = markRaw(patch.zipFile);
            entry.stats = {
                totalStops: this.stats.totalStops,
                totalRoutes: this.stats.totalRoutes,
                totalTrips: this.stats.totalTrips,
            };
            entry.tableCounts = { ...this.tableCounts };
            if (patch.analysisTables !== undefined) {
                entry.analysisTables = patch.analysisTables
                    ? markRaw(patch.analysisTables)
                    : null;
                entry.analysisHydrated = Boolean(patch.analysisTables);
            }
        },
        /**
         * @deprecated Ağır tablo stash OOM yapıyordu — yalnızca meta/zip senkronlar.
         * Switch için parkActiveFeedBeforeSwitch kullanın.
         */
        stashActiveFeedAnalysisTables() {
            const entry = this.activeFeed;
            if (!entry || !this.currentGTFSData)
                return;
            entry.stats = {
                totalStops: this.stats.totalStops,
                totalRoutes: this.stats.totalRoutes,
                totalTrips: this.stats.totalTrips,
            };
            entry.tableCounts = { ...this.tableCounts };
            const zip = this.currentGTFSData.originalFile ||
                null;
            if (zip)
                entry.zipFile = markRaw(zip);
            // Ağır tabloları registry'de tutma
            entry.analysisTables = null;
            entry.analysisHydrated = false;
        },
        /** Tüm feed'lerdeki geçici analiz cache'ini bırak */
        clearAllAnalysisCaches() {
            for (const entry of this.feeds) {
                entry.analysisTables = null;
                entry.analysisHydrated = false;
            }
        },
        clearActiveEditorState() {
            this.currentGTFSData = null;
            this.feedFileName = "";
            this.stats = {
                ...defaultStats(),
                totalFeeds: this.feeds.length,
                validFeeds: this.feeds.length,
            };
            this.tableCounts = defaultTableCounts();
            this.agencyComplete = false;
            this.feedInfoComplete = false;
            this.pendingDirty = false;
            this.hydratingTables = false;
            this.tableLoadCacheKey = "";
            this.clearHistory();
            this._invalidateIndexes();
        },
        findFeed(localId) {
            return this.feeds.find((f) => f.localId === localId) || null;
        },
        findFeedByDbId(dbId) {
            return this.feeds.find((f) => f.dbId === Number(dbId)) || null;
        },
        toggleIncludeInAnalysis(localId, value) {
            const entry = this.findFeed(localId);
            if (!entry)
                return;
            entry.includeInAnalysis =
                value !== undefined ? Boolean(value) : !entry.includeInAnalysis;
        },
        setFeedAnalysisTables(localId, tables) {
            const entry = this.findFeed(localId);
            if (!entry)
                return;
            entry.analysisTables = markRaw(tables);
            entry.analysisHydrated = true;
        },
        updateFeedEntry(localId, patch) {
            const entry = this.findFeed(localId);
            if (!entry)
                return;
            Object.assign(entry, patch);
            if (patch.zipFile)
                entry.zipFile = markRaw(patch.zipFile);
            if (patch.analysisTables) {
                entry.analysisTables = markRaw(patch.analysisTables);
                entry.analysisHydrated = true;
            }
        },
        removeFeed(localId) {
            const idx = this.feeds.findIndex((f) => f.localId === localId);
            if (idx < 0) {
                return {
                    removed: false,
                    needReload: false,
                    nextId: this.activeFeedId,
                };
            }
            const wasActive = this.activeFeedId === localId;
            this.feeds.splice(idx, 1);
            this.stats.totalFeeds = this.feeds.length;
            this.stats.validFeeds = this.feeds.length;
            if (!wasActive) {
                return { removed: true, needReload: false, nextId: this.activeFeedId };
            }
            const next = this.feeds[0] || null;
            this.activeFeedId = next?.localId || null;
            this.clearActiveEditorState();
            return {
                removed: true,
                needReload: Boolean(next),
                nextId: next?.localId || null,
            };
        },
        renameFeed(localId, name) {
            const entry = this.findFeed(localId);
            if (!entry)
                return;
            const n = String(name || "").trim();
            if (!n)
                return;
            entry.name = n;
        },
        getTableRows(tableName) {
            const data = this.currentGTFSData;
            if (!data)
                return [];
            const key = `${tableName}.txt`;
            const table = data.meta?.tables?.[key];
            if (table) {
                if (!Array.isArray(table.sample_data))
                    table.sample_data = [];
                return table.sample_data;
            }
            const fromInfo = data.tables_info?.[key]?.sample_data;
            return Array.isArray(fromInfo) ? fromInfo : [];
        },
        _syncTableCount(tableName) {
            const rows = this.getTableRows(tableName);
            const count = rows.length;
            if (tableName in this.tableCounts) {
                this.tableCounts[tableName] = count;
            }
            const data = this.currentGTFSData;
            const table = data?.meta?.tables?.[`${tableName}.txt`];
            if (table)
                table.record_count = count;
            if (tableName === "routes")
                this.stats.totalRoutes = count;
            if (tableName === "stops")
                this.stats.totalStops = count;
            if (tableName === "trips")
                this.stats.totalTrips = count;
        },
        updateTableRow(tableName, idKey, idValue, patch, opts = {}) {
            const rows = this.getTableRows(tableName);
            const idx = rows.findIndex((r) => String(r[idKey]) === String(idValue));
            if (idx === -1)
                return false;
            this.pushHistory(tableName, opts.label || updateLabel(tableName, patch));
            rows[idx] = { ...rows[idx], ...patch };
            this.pendingDirty = true;
            this._invalidateIndexes();
            return true;
        },
        deleteTableRows(tableName, idKey, idValues, opts = {}) {
            const ids = new Set(idValues.map(String));
            const rows = this.getTableRows(tableName);
            const next = rows.filter((r) => !ids.has(String(r[idKey])));
            if (next.length !== rows.length) {
                this.pushHistory(tableName, opts.label || historyCountLabel(tableName, ids.size, "silindi"));
                replaceRowsInPlace(rows, next);
            }
            this._syncTableCount(tableName);
            this.pendingDirty = true;
            this._invalidateIndexes();
            return idValues.length;
        },
        /** Bir shape_id'ye ait tüm satırları yeni satırlarla değiştirir (tek geçmiş adımı). */
        replaceShapeRows(shapeId, newRows, opts = {}) {
            this.pushHistory("shapes", opts.label || "Şekil düzenlendi");
            const all = this.getTableRows("shapes");
            const sid = String(shapeId);
            const kept = [];
            for (let i = 0; i < all.length; i++) {
                const row = all[i];
                if (String(row["shape_id"]) !== sid)
                    kept.push(row);
            }
            // spread (...filtered, ...newRows) büyük tabloda stack overflow yapar
            all.length = 0;
            for (let i = 0; i < kept.length; i++)
                all.push(kept[i]);
            for (let i = 0; i < newRows.length; i++)
                all.push({ ...newRows[i] });
            this._syncTableCount("shapes");
            this.pendingDirty = true;
            this._invalidateIndexes();
        },
        /** Bir shape_id'yi (ve bağlı trip.shape_id'lerini) yeniden adlandırır. */
        renameShape(oldId, newId) {
            const sid = String(oldId);
            const nid = String(newId).trim();
            if (!nid || sid === nid)
                return false;
            const shapes = this.getTableRows("shapes");
            const trips = this.getTableRows("trips");
            // Çakışma kontrolü
            if (shapes.some((r) => String(r["shape_id"]) === nid))
                return false;
            this.pushHistory(["shapes", "trips"], "Şekil yeniden adlandırıldı");
            // Satırı yerinde değiştirme — geçmiş farkı satır referansına dayanır
            for (let i = 0; i < shapes.length; i++) {
                if (String(shapes[i]["shape_id"]) === sid) {
                    shapes[i] = { ...shapes[i], shape_id: nid };
                }
            }
            for (let i = 0; i < trips.length; i++) {
                if (String(trips[i]["shape_id"]) === sid) {
                    trips[i] = { ...trips[i], shape_id: nid };
                }
            }
            this._syncTableCount("shapes");
            this.pendingDirty = true;
            this._invalidateIndexes();
            return true;
        },
        duplicateTableRow(tableName, idKey, idValue) {
            const rows = this.getTableRows(tableName);
            const src = rows.find((r) => String(r[idKey]) === String(idValue));
            if (!src)
                return null;
            const copy = { ...src };
            const base = String(src[idKey] ?? "copy");
            let n = 1;
            let nextId = `${base}_copy`;
            while (rows.some((r) => String(r[idKey]) === nextId)) {
                n += 1;
                nextId = `${base}_copy${n}`;
            }
            copy[idKey] = nextId;
            this.pushHistory(tableName, historyCountLabel(tableName, 1, "kopyalandı"));
            rows.push(copy);
            this._syncTableCount(tableName);
            this.pendingDirty = true;
            this._invalidateIndexes();
            return copy;
        },
        addTableRow(tableName, row, opts = {}) {
            this.pushHistory(tableName, opts.label || historyCountLabel(tableName, 1, "eklendi"));
            const rows = this.getTableRows(tableName);
            rows.push({ ...row });
            this._syncTableCount(tableName);
            this.pendingDirty = true;
            this._invalidateIndexes();
            return row;
        },
        addTableRows(tableName, newRows, opts = {}) {
            if (!Array.isArray(newRows) || newRows.length === 0)
                return 0;
            this.pushHistory(tableName, opts.label || historyCountLabel(tableName, newRows.length, "eklendi"));
            const rows = this.getTableRows(tableName);
            for (const row of newRows) {
                rows.push({ ...row });
            }
            this._syncTableCount(tableName);
            this.pendingDirty = true;
            this._invalidateIndexes();
            return newRows.length;
        },
        /** Tek history kaydıyla birden fazla satırı patch'le (UI kilitlenmesini önler). */
        updateTableRowsWhere(tableName, matchFn, patch, opts = {}) {
            const rows = this.getTableRows(tableName);
            const hits = [];
            for (let i = 0; i < rows.length; i++) {
                if (matchFn(rows[i]))
                    hits.push(i);
            }
            if (!hits.length)
                return 0;
            this.pushHistory(tableName, opts.label || historyCountLabel(tableName, hits.length, "güncellendi"));
            for (const i of hits)
                rows[i] = { ...rows[i], ...patch };
            this.pendingDirty = true;
            this._invalidateIndexes();
            return hits.length;
        },
        removeTableRowsWhere(tableName, matchFn, opts = {}) {
            const rows = this.getTableRows(tableName);
            const next = rows.filter((r) => !matchFn(r));
            const removed = rows.length - next.length;
            if (removed <= 0)
                return 0;
            this.pushHistory(tableName, opts.label || historyCountLabel(tableName, removed, "silindi"));
            replaceRowsInPlace(rows, next);
            this._syncTableCount(tableName);
            this.pendingDirty = true;
            this._invalidateIndexes();
            return removed;
        },
        upsertStopTime(tripId, stopId, patch, opts = {}) {
            const getRows = (name) => this.getTableRows(name);
            const rows = this.getTableRows("stop_times");
            this.pushHistory("stop_times", opts.label || "Sefer saatleri değişti");
            const existing = findStopTimeRow(getRows, tripId, stopId);
            let row;
            let isNew = false;
            if (!existing) {
                isNew = true;
                const seq = patch.stop_sequence ??
                    rows.filter((r) => String(r.trip_id) === String(tripId)).length + 1;
                row = {
                    trip_id: tripId,
                    stop_id: stopId,
                    stop_sequence: seq,
                    arrival_time: patch.arrival_time ?? "",
                    departure_time: patch.departure_time ?? patch.arrival_time ?? "",
                    ...patch,
                };
                rows.push(row);
            }
            else {
                row = { ...existing, ...patch };
                const idx = rows.indexOf(existing);
                if (idx >= 0)
                    rows[idx] = row;
                else
                    rows.push(row);
            }
            patchStopTimeIndex(tripId, stopId, row, isNew);
            this._syncTableCount("stop_times");
            this.pendingDirty = true;
            // Full index rebuild yok — sadece UI epoch
            this.tableEpoch += 1;
        },
        markStopTimeSkipped(tripId, stopId) {
            // SKIP: pickup_type=1 & drop_off_type=1 (GTFS convention for "no pickup/drop-off")
            this.upsertStopTime(tripId, stopId, {
                pickup_type: "1",
                drop_off_type: "1",
                arrival_time: "",
                departure_time: "",
            }, { label: "Durak atlandı" });
        },
        deleteTrips(tripIds, opts = {}) {
            const idSet = new Set(tripIds.map(String));
            this.pushHistory(["trips", "stop_times"], opts.label || historyCountLabel("trips", idSet.size, "silindi"));
            const tripRows = this.getTableRows("trips");
            const nextTrips = tripRows.filter((r) => !idSet.has(String(r.trip_id)));
            replaceRowsInPlace(tripRows, nextTrips);
            this._syncTableCount("trips");
            const times = this.getTableRows("stop_times");
            const next = times.filter((r) => !idSet.has(String(r.trip_id)));
            replaceRowsInPlace(times, next);
            this._syncTableCount("stop_times");
            this.pendingDirty = true;
            this._invalidateIndexes();
        },
        toggleRouteHidden(routeId) {
            const id = String(routeId);
            const idx = this.hiddenRouteIds.indexOf(id);
            if (idx >= 0)
                this.hiddenRouteIds.splice(idx, 1);
            else
                this.hiddenRouteIds.push(id);
        },
        reset() {
            this.activeTab = "builder";
            this.builderCurrentStep = 1;
            this.isProcessing = false;
            this.currentGTFSData = null;
            this.feedFileName = "";
            this.stats = defaultStats();
            this.tableCounts = defaultTableCounts();
            this.agencyComplete = false;
            this.feedInfoComplete = false;
            this.pendingDirty = false;
            this.hydratingTables = false;
            this.tableLoadCacheKey = "";
            this.tableEpoch = 0;
            this._batchDepth = 0;
            this._batchTables = [];
            this._pendingInvalidate = false;
            this.feeds = [];
            this.activeFeedId = null;
            this.analysisHydrating = false;
            this.hiddenRouteIds = [];
            this.clearHistory();
            this._invalidateIndexes();
        },
    },
});
